// UsersPicker — inline company-user selection list: one bordered card holding a
// search field above a scrollable list of avatar rows (name + optional email).
// Controlled selection (id array in/out, company-user ids — the same space the
// selection endpoint and the group/project write payloads use). Options are
// self-fetched from the users selection endpoint; `skip` holds that fetch until
// the host modal/drawer opens.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Avatar from './Avatar';
import Icon from './Icon';
import Input from './Input';
import { useGetUsersSelectionQuery } from '@/api/endpoints/usersApi';
import toneOf from '@/utils/avatarTone';
import initialsOf from '@/utils/initials';

export interface UsersPickerProps {
  /** Selected company-user ids. */
  value: string[];
  onChange: (ids: string[]) => void;
  /** Hold the options fetch until the host surface is open. */
  skip?: boolean;
  /** Freeze the rows (read-only surfaces). */
  disabled?: boolean;
  className?: string;
}

const UsersPicker = ({
  value,
  onChange,
  skip = false,
  disabled = false,
  className = '',
}: UsersPickerProps) => {
  const { t } = useTranslation();
  const { data: users = [], isLoading } = useGetUsersSelectionQuery(undefined, { skip });
  const [search, setSearch] = useState('');

  const q = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      !q
        ? users
        : users.filter(
            (u) => u.name.toLowerCase().includes(q) || (u.email ?? '').toLowerCase().includes(q),
          ),
    [users, q],
  );

  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);

  return (
    <div className={`users-picker${className ? ` ${className}` : ''}`}>
      <div className='users-picker__search'>
        <Input
          size='sm'
          iconStart='search'
          placeholder={t('users_picker_search_ph')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={disabled}
        />
      </div>
      <div className='users-picker__list'>
        {/* Only an empty result deserves the message — during the first fetch the
            list is legitimately empty and the copy would flash. */}
        {filtered.length === 0
          ? !isLoading && <p className='users-picker__empty'>{t('users_picker_empty')}</p>
          : filtered.map((u) => {
              const selected = value.includes(u.id);
              return (
                <button
                  key={u.id}
                  type='button'
                  className={`users-picker__row${selected ? ' is-selected' : ''}`}
                  aria-pressed={selected}
                  disabled={disabled}
                  onClick={() => toggle(u.id)}
                >
                  <span className={`users-picker__check${selected ? ' is-checked' : ''}`}>
                    {selected && <Icon name='check' size={13} />}
                  </span>
                  <Avatar initials={initialsOf(u.name)} tone={toneOf(u.id)} size={32} />
                  <span className='users-picker__identity'>
                    <span className='users-picker__name'>{u.name}</span>
                    {u.email && <span className='users-picker__email'>{u.email}</span>}
                  </span>
                </button>
              );
            })}
      </div>
    </div>
  );
};

export default UsersPicker;
